/**
 * SSO Error Tracker for structured Sentry reporting
 *
 * Provides consistent error tracking for SSO authentication failures
 * with Azure AD error code extraction and domain/provider tagging.
 */

import * as Sentry from "@sentry/react";
import { logger } from "@/lib/logger";

export type SsoErrorType =
  | "login_start" // Error when initiating SSO login
  | "callback" // Error from OAuth provider callback
  | "code_exchange" // Error during code-for-session exchange
  | "session" // Error checking/creating session
  | "config"; // SSO configuration error

export interface SsoErrorContext {
  /** Type of SSO error */
  errorType: SsoErrorType;
  /** Email domain used for SSO (e.g., "example.com") */
  domain?: string;
  /** Supabase SSO provider ID (e.g., "sso:example-provider") */
  providerId?: string;
  /** Azure AD error code (e.g., "AADSTS700016") */
  azureErrorCode?: string;
  /** Human-readable error message */
  errorMessage: string;
  /** Whether this is an admin/configuration error vs user error */
  isAdminError: boolean;
  /** Optional additional context */
  extra?: Record<string, unknown>;
}

/**
 * Extract Azure AD error code (AADSTS) from error message
 *
 * Azure AD errors follow the format AADSTS followed by numbers
 * @example "AADSTS700016: Application with identifier 'xxx' was not found"
 */
export function extractAzureErrorCode(message: string): string | undefined {
  if (!message) return undefined;
  const match = message.match(/AADSTS\d+/i);
  return match ? match[0].toUpperCase() : undefined;
}

/**
 * Track an SSO error to Sentry with structured tags
 *
 * This enables filtering in Sentry by:
 * - `sso.error_type` - Type of error (login_start, callback, etc.)
 * - `sso.domain` - Email domain
 * - `sso.provider_id` - SSO provider ID
 * - `sso.azure_error_code` - Azure AD error code (AADSTS)
 * - `sso.is_admin_error` - Whether this is an admin-facing error
 */
export function trackSsoError(context: SsoErrorContext): void {
  // Also extract Azure error code from message if not explicitly provided
  const azureCode = context.azureErrorCode || extractAzureErrorCode(context.errorMessage);

  // Structured local log for the SSO auth path. PII-safe: only error type, email
  // DOMAIN, provider id, Azure code and the operational message — never an address,
  // token or full credential.
  //
  // Uses `logger.warn` (not `error`): this function already reports the single
  // intended Sentry event via `Sentry.captureMessage` below. `logger.error`
  // auto-reports to Sentry too, which would create a duplicate event. `warn` is
  // visible in the prod console (default prod level is `warn`) but never auto-Sentries.
  logger.warn("SSO error", {
    errorType: context.errorType,
    domain: context.domain,
    providerId: context.providerId,
    azureErrorCode: azureCode,
    message: context.errorMessage,
  });

  Sentry.withScope((scope) => {
    // Set structured tags for filtering
    scope.setTag("sso.error_type", context.errorType);
    scope.setTag("sso.is_admin_error", String(context.isAdminError));

    if (context.domain) {
      scope.setTag("sso.domain", context.domain);
    }

    if (context.providerId) {
      scope.setTag("sso.provider_id", context.providerId);
    }

    if (azureCode) {
      scope.setTag("sso.azure_error_code", azureCode);
    }

    // Add extra context as extras (not tags - for larger data)
    if (context.extra) {
      scope.setExtras(context.extra);
    }

    // Capture as error-level message
    const prefix = azureCode ? `SSO Error [${azureCode}]` : "SSO Error";
    Sentry.captureMessage(`${prefix}: ${context.errorMessage}`, "error");
  });
}

/**
 * Convenience function to track SSO login start errors
 */
export function trackSsoLoginStartError(
  domain: string,
  errorMessage: string,
  extra?: Record<string, unknown>,
): void {
  trackSsoError({
    errorType: "login_start",
    domain,
    errorMessage,
    isAdminError: false,
    extra,
  });
}

/**
 * Convenience function to track SSO callback errors
 */
export function trackSsoCallbackError(
  errorMessage: string,
  options: {
    domain?: string;
    providerId?: string;
    isAdminError?: boolean;
    extra?: Record<string, unknown>;
  } = {},
): void {
  trackSsoError({
    errorType: "callback",
    domain: options.domain,
    providerId: options.providerId,
    errorMessage,
    isAdminError: options.isAdminError ?? false,
    extra: options.extra,
  });
}

/**
 * Convenience function to track code exchange errors
 */
export function trackSsoCodeExchangeError(
  errorMessage: string,
  options: {
    isAdminError?: boolean;
    extra?: Record<string, unknown>;
  } = {},
): void {
  trackSsoError({
    errorType: "code_exchange",
    errorMessage,
    isAdminError: options.isAdminError ?? false,
    extra: options.extra,
  });
}
